import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthProvider';
import { colaboradorService } from '@/features/colaboradores/colaboradorService';
import { useToast } from '@/shared/hooks/useToast';
import { useRealtimeEvento } from '@/shared/lib/realtime/useRealtime';
import type { RealtimeEvento } from '@/shared/lib/realtime/realtimeContext';
import type { Colaborador } from '@/types/api';
import { CHAT_ACAO } from './chatAcoes';
import { chatKeys } from './useChatQueries';
import {
  nomeAutorMensagem,
  TIPO_MENSAGEM_CHAT,
  type ConversaResumo,
  type Mensagem,
} from './chatTypes';

function idColaboradorDaSessao(
  lista: Colaborador[] | undefined,
  login: string | undefined,
): number | null {
  if (!lista || !login) return null;
  const match =
    lista.find((c) => c.login === login) ?? lista.find((c) => c.usuario?.login === login);
  return match?.id ?? null;
}

function previewMensagem(mensagem: Mensagem): string {
  if (mensagem.tipo === TIPO_MENSAGEM_CHAT.IMAGEM) return 'Imagem';
  if (mensagem.tipo === TIPO_MENSAGEM_CHAT.ARQUIVO) return 'Arquivo';
  if (mensagem.tipo === TIPO_MENSAGEM_CHAT.AUDIO) {
    const duracao = mensagem.duracaoSegundos;
    if (duracao != null && duracao > 0) {
      const minutos = Math.floor(duracao / 60);
      const segundos = duracao % 60;
      return `Audio ${minutos}:${segundos < 10 ? '0' : ''}${segundos}`;
    }
    return 'Audio';
  }
  const texto = mensagem.texto?.trim();
  return texto || 'Nova mensagem';
}

function acharConversa(
  queryClient: ReturnType<typeof useQueryClient>,
  idConversa: number,
): ConversaResumo | undefined {
  const listas = queryClient.getQueriesData<ConversaResumo[]>({ queryKey: chatKeys.conversas() });
  for (const [, data] of listas) {
    const achou = data?.find((c) => c.id === idConversa);
    if (achou) return achou;
  }
  const detalhes = queryClient.getQueriesData<ConversaResumo>({
    queryKey: chatKeys.conversa(idConversa),
  });
  for (const [, data] of detalhes) {
    if (data?.id === idConversa) return data;
  }
  return undefined;
}

/** Titulo e corpo no mesmo criterio do push (R18): remetente ou grupo, e a preview. */
export function textoToastChat(
  mensagem: Mensagem,
  conversa: ConversaResumo | undefined,
): string {
  const remetente = nomeAutorMensagem(mensagem).trim();
  const tituloGrupo = conversa?.tipo === 'GRUPO' ? conversa.titulo?.trim() || 'Grupo' : '';
  const titulo = tituloGrupo || remetente || 'Chat';
  return `${titulo}: ${previewMensagem(mensagem)}`;
}

/**
 * Toast de mensagem nova com a web da escola aberta. O push FCM nao sai
 * enquanto o STOMP esta online (R17); o aviso in-app cobre esse caso.
 */
export function useChatMensagemToast() {
  const { user } = useAuth();
  const enabled = Boolean(user?.acoes?.includes(CHAT_ACAO.VER));
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const vistos = useRef(new Set<number>());

  const colaboradoresQuery = useQuery({
    queryKey: ['colaboradores', 'me-match', user?.login],
    queryFn: () => colaboradorService.listar(),
    enabled: enabled && Boolean(user?.login),
    staleTime: 60_000,
  });

  const onEvento = useCallback(
    (evento: RealtimeEvento) => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.resumo() });
      void queryClient.invalidateQueries({ queryKey: chatKeys.conversas() });

      const mensagem = evento.payload.mensagem as Mensagem | undefined;
      if (!mensagem || typeof mensagem.id !== 'number') return;
      if (vistos.current.has(mensagem.id)) return;

      const idConversa = Number(evento.payload.idConversa ?? mensagem.idConversa);
      const idMe = idColaboradorDaSessao(colaboradoresQuery.data, user?.login);
      if (idMe != null && mensagem.idColaboradorAutor === idMe) return;
      if (
        idMe == null &&
        user?.nome &&
        nomeAutorMensagem(mensagem).trim() === user.nome.trim()
      ) {
        return;
      }
      if (idConversa && pathname === `/chat/${idConversa}`) return;

      vistos.current.add(mensagem.id);
      const conversa = Number.isFinite(idConversa)
        ? acharConversa(queryClient, idConversa)
        : undefined;
      const destino = idConversa > 0 ? `/chat/${idConversa}` : null;
      toast.info(textoToastChat(mensagem, conversa), {
        onClick: destino ? () => navigate(destino) : undefined,
      });
    },
    [colaboradoresQuery.data, navigate, pathname, queryClient, toast, user?.login, user?.nome],
  );

  useRealtimeEvento('MENSAGEM_NOVA', onEvento, enabled);
}
